import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "koltprinting_cart_v1";

// Cart data lives in memory; we mirror to sessionStorage (NOT localStorage)
// so it survives page navigation/refresh within the same tab but is wiped
// when the browser tab closes. That reduces the attack surface (any XSS
// on a fresh page load can no longer read a stale cart) and matches the
// expected lifecycle of a reorder session.
//
// IMPORTANT: hydration MUST happen synchronously inside the useState lazy
// initialiser.  Reading sessionStorage in a useEffect causes a race where
// the persist-effect fires first with the initial [] and overwrites the
// stored cart before hydration has a chance to apply — wiping the cart on
// the first navigation.
function readInitial() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (err) {
    console.warn("[cart] failed to restore from sessionStorage:", err);
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(readInitial);

  // Persist on every change.  Safe to run on first commit too — the value
  // already matches sessionStorage (or [] if storage was empty) so we
  // can't overwrite live data.
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (err) {
      console.warn("[cart] failed to persist to sessionStorage:", err);
    }
  }, [items]);

  const addItem = useCallback((item) => {
    setItems((prev) => [
      ...prev,
      { ...item, item_id: `cart_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` },
    ]);
  }, []);

  const removeItem = useCallback((item_id) => {
    setItems((prev) => prev.filter((i) => i.item_id !== item_id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const { subtotal, count } = useMemo(() => {
    let s = 0;
    let n = 0;
    for (const i of items) {
      s += i.line_total || 0;
      n += i.quantity || 0;
    }
    return { subtotal: s, count: n };
  }, [items]);

  // Memoise the context value so consumers don't re-render on every parent render.
  const value = useMemo(
    () => ({ items, addItem, removeItem, clear, subtotal, count }),
    [items, addItem, removeItem, clear, subtotal, count],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  return useContext(CartContext);
}
